/* global WebKitMutationObserver */
const behaviorClickDataAttr = '[data-behavior-click]';

const attrChangeEventHandler = () => {
    unregister();
    register();
};

// eslint-disable-next-line complexity
const containerClickEventHandler = evt => {
    evt.stopPropagation();

    const containerTarget = evt.currentTarget,
        linkSelector = containerTarget.getAttribute('data-click-target'),
        linkElement = containerTarget.querySelector(linkSelector) || containerTarget.parentNode.querySelector(linkSelector);

    if(linkElement && linkElement !== (evt.srcElement || evt.target)) {
        linkElement.click();
    }
};

const hasDataClickTarget = domElement => domElement.dataset && domElement.dataset.clickTarget !== null;

/**
 * @description Go back up in the DOM to look for the elements that need to be connected to the link element (parent, grand-parent or parent siblings).
 * @param {HTMLElement} domElement - Dom element.
 * @returns {HTMLElement[]} - Elements that match.
 */
const getDataClickTargetElements = domElement => {
    const dataClickTargetElements = [];
    const parentEl = domElement.parentNode;

    // NOTE: replace the first "if"s by a more traversal method
    if(hasDataClickTarget(parentEl)) {
        dataClickTargetElements.push(parentEl);
    } else if(hasDataClickTarget(parentEl.parentNode)) {
        dataClickTargetElements.push(parentEl.parentNode);
    } else if(hasDataClickTarget(parentEl.parentNode.parentNode)) {
        dataClickTargetElements.push(parentEl.parentNode.parentNode);
    } else {
        const domSiblingNodes = [...parentEl.parentNode.childNodes];

        domSiblingNodes.forEach(domSiblingNode => {
            if((domSiblingNode.nodeType !== Node.TEXT_NODE) && hasDataClickTarget(domSiblingNode)) {
                dataClickTargetElements.push(domSiblingNode);
            }
        });
    }

    return dataClickTargetElements;
};

const getNodeList = domNodeList => {
    if(!domNodeList || domNodeList.length < 1) {
        return [...document.querySelectorAll(behaviorClickDataAttr)];
    }

    return [...domNodeList]
        .reduce((acc, domCurrentNode) => {
            if(domCurrentNode.nodeType) {
                return [
                    ...acc,
                    domCurrentNode.querySelector(behaviorClickDataAttr)
                ];
            }
            return [...acc];
        }, []);
};

/**
 * @description Unregister click behavior on all element(s) with data-behavior-click attribute..
 * @param {HTMLElement[]} [domNodeList] - The node list.
 */
export const unregister = domNodeList => {
    const domNodeCollection = getNodeList(domNodeList);

    domNodeCollection.forEach(domElement => {
        domElement.parentNode.removeEventListener('click', containerClickEventHandler);
        domElement.removeEventListener('DOMAttrModified', attrChangeEventHandler);

        if(domElement.observer) {
            domElement.observer.disconnect();
        }
    });
};

const observeDom = domElement => {
    if(typeof WebKitMutationObserver !== 'undefined') {
        domElement.observer = domElement.observer || new WebKitMutationObserver(attrChangeEventHandler);
        domElement.observer.observe(domElement, {attributes: true});
    } else {
        domElement.addEventListener('DOMAttrModified', attrChangeEventHandler, false);
    }
};

/**
 * @description Register click behavior on all element(s) with data-behavior-click attribute.
 * @param {HTMLElement[]} [domNodeList] - The node list.
 */
export const register = domNodeList => {
    const domNodeCollection = getNodeList(domNodeList);

    domNodeCollection.forEach(domElement => {
        observeDom(domElement);

        const clickType = domElement.dataset && domElement.dataset.behaviorClick;
        const dataClickTargetElements = getDataClickTargetElements(domElement);

        if(clickType === 'container-click' && dataClickTargetElements.length > 0) {
            dataClickTargetElements.forEach(domTargetElement => {
                domTargetElement.addEventListener('click', containerClickEventHandler);
            });
        }
    });
};
